import { Box, Link, MenuItem, SvgIcon, Tooltip, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { CounterAdornment } from '../../Components/CounterAdornment';
import { NAV_COLOR_PALETTE, tooltipOffsetProps } from '../../Components/styleConstants';
import { useMenuContext } from '../../ContextState/useMenuContext';

import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { IMenuOption } from '../../types';
import { useIsContentOverflowing } from '../../utils/hooks/useIsContentOverflowing';
import { itemIsLeafNode } from '../../utils/utils';

import { useMobileKeyboardNavContext } from '../Context/MobileKeyboardNavContext';
interface IMobileLevelOneMenuItemProps {
  menuItem: IMenuOption;
  isFirstMobileMenuItem: boolean;
}
export const MobileLevelOneMenuItem: React.FC<IMobileLevelOneMenuItemProps> = ({ menuItem, isFirstMobileMenuItem }) => {
  const { setCurrentLevelOneItem, setShowMainSubmenu } = useMenuContext();
  const { mobileLevelOneMenuItemIdToFocusNext, firstMobileMenuItemRef, setMobileLevelOneMenuItemIdToFocusNext } =
    useMobileKeyboardNavContext();
  const labelRef = React.useRef<HTMLAnchorElement>(null);
  const contentIsOverflowing = useIsContentOverflowing(labelRef, menuItem.label);
  const tooltipTitle = menuItem?.tooltipText || (contentIsOverflowing ? menuItem.label : '');
  const isLeafNode = itemIsLeafNode(menuItem);
  const dataTest = menuItem.url
    ? getDynamicDataTestId(DATA_TEST.mobile_level_one_link, menuItem.id)
    : getDynamicDataTestId(DATA_TEST.mobile_level_one_item, menuItem.id);
  const handleNonLeafNodeClick = () => {
    setCurrentLevelOneItem(menuItem);
    setMobileLevelOneMenuItemIdToFocusNext(menuItem.id);
  };

  let MenuItemComponent: React.ElementType;

  if (menuItem.linkComponent) {
    MenuItemComponent = menuItem.linkComponent;
  } else if (menuItem.url) {
    MenuItemComponent = Link;
  } else {
    MenuItemComponent = Typography;
  }

  return (
    <Tooltip
      data-test={DATA_TEST.level_one_more_items_tooltip}
      title={tooltipTitle}
      enterNextDelay={500}
      placement="top"
      disableInteractive={true}
      {...tooltipOffsetProps}
    >
      <MenuItem
        data-test={dataTest}
        data-og={getDynamicDataOGId(DATA_OG.menu_item, menuItem.label)}
        onClick={() => {
          // if there is an onClick handler run it & update the current level one item
          if (menuItem.onClick) {
            menuItem.onClick();
            setShowMainSubmenu(false);
            return;
          }
          // if there is a URL navigate the user and do not run any subsequent logic
          if (menuItem.url) {
            labelRef?.current?.click();
            setShowMainSubmenu(false);
            return;
          }
          // Otherwise assume there are children (or they need to be fetched) and set the current level one item
          handleNonLeafNodeClick();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' && !isLeafNode) {
            // Clicks itself to invoke the existing onClick handler
            e.currentTarget.click();
          }
        }}
        ref={isFirstMobileMenuItem ? firstMobileMenuItemRef : null}
        autoFocus={isFirstMobileMenuItem || mobileLevelOneMenuItemIdToFocusNext === menuItem.id}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: (theme) => `${theme.spacing(0.5)} 0`,
          px: 2,
          minHeight: '40px',
          width: '100%'
        }}
        aria-label={
          menuItem.counterAdornmentValue
            ? `${menuItem.label} counter value ${menuItem.counterAdornmentValue}`
            : menuItem.label
        }
      >
        <Box display="flex">
          <Link
            ref={labelRef}
            variant="h4"
            sx={{
              color: NAV_COLOR_PALETTE.primaryGray,
              ':visited': { color: NAV_COLOR_PALETTE.primaryGray }
            }}
            component={MenuItemComponent}
            href={menuItem.url}
            target={menuItem.openInNewTab ? '_blank' : undefined}
            textAlign="center"
            lineHeight="1rem"
            underline="none"
          >
            {menuItem.label}
          </Link>
          {Boolean(menuItem.counterAdornmentValue) && menuItem.counterAdornmentValue >= 0 && (
            <CounterAdornment count={menuItem.counterAdornmentValue} />
          )}
        </Box>
        {!isLeafNode && (
          <SvgIcon
            inheritViewBox
            component={ChevronRightIcon}
            data-test={DATA_TEST.mobile_chevron_right_icon}
            sx={{ color: NAV_COLOR_PALETTE.primaryGray }}
          />
        )}
      </MenuItem>
    </Tooltip>
  );
};
